import React from 'react';
import clsx from 'clsx';
import { useIntl } from 'react-intl';
import { Input } from 'reactstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBoxArchive } from '@fortawesome/pro-light-svg-icons/faBoxArchive';
import { faCheck } from '@fortawesome/pro-light-svg-icons/faCheck';
import { TypedMemo } from '../../../common/types';
import { CatalogItem } from '../../../../redux/api/fymAPI.types';
import { platformMeta } from '../platformMeta';
import { POSTS_PLATFORMS, PostsPlatform } from './postsModel';
import { postsMessages } from './postsMessages';
/* eslint-disable css-modules/no-unused-class -- a shared stylesheet; each part uses its own slice of it */
import styles from './Posts.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface PostsToolbarProps {
  /** The song filter; absent on the release Posts tab (the song is set). */
  songs?: ReadonlyArray<CatalogItem>;
  song: string | undefined;
  onSong: (catalogItemUuid: string | undefined) => void;
  platform: PostsPlatform | undefined;
  onPlatform: (platform: PostsPlatform | undefined) => void;
  /** Null hides "Select" (the demo artist, design d2). */
  onSelect: (() => void) | null;
  isSelecting: boolean;
  archivedCount: number;
  onArchive: () => void;
}

/**
 * The toolbar of the board (design assets-rethink c1 `.al-tbar`): the song filter, the channel
 * chips, "Select" and "Archive · N" (while the archive has files).
 */
const PostsToolbarComponent: React.FC<PostsToolbarProps> = (props) => {
  const { songs, song, onSong, platform, onPlatform, onSelect, isSelecting, archivedCount, onArchive } = props;
  const intl = useIntl();
  return (
    <div className={styles.toolbar} data-testid="posts-toolbar">
      {songs !== undefined && (
        <Input
          type="select"
          bsSize="sm"
          className={styles.songSelect}
          aria-label={intl.formatMessage(postsMessages.songFilter)}
          value={song ?? ''}
          onChange={(event) => onSong(event.target.value === '' ? undefined : event.target.value)}
        >
          <option value="">{intl.formatMessage(postsMessages.allSongs)}</option>
          {songs.map((item) => (
            <option key={item.uuid} value={item.uuid}>
              {item.title}
            </option>
          ))}
        </Input>
      )}
      <div className={styles.chips} role="group" aria-label={intl.formatMessage(postsMessages.channelFilter)}>
        <button
          type="button"
          className={clsx(styles.chip, platform === undefined && styles.chipOn)}
          aria-pressed={platform === undefined}
          onClick={() => onPlatform(undefined)}
        >
          {intl.formatMessage(postsMessages.allChannels)}
        </button>
        {POSTS_PLATFORMS.map((one) => (
          <button
            key={one}
            type="button"
            className={clsx(styles.chip, platform === one && styles.chipOn)}
            aria-pressed={platform === one}
            onClick={() => onPlatform(one)}
          >
            {platformMeta[one].name}
          </button>
        ))}
      </div>
      <span className={styles.grow} />
      {onSelect !== null && !isSelecting && (
        <button type="button" className={styles.link} onClick={onSelect} data-testid="posts-select">
          <FontAwesomeIcon icon={faCheck} />
          {intl.formatMessage(postsMessages.select)}
        </button>
      )}
      {archivedCount > 0 && (
        <button type="button" className={styles.link} onClick={onArchive} data-testid="posts-archive-link">
          <FontAwesomeIcon icon={faBoxArchive} />
          {intl.formatMessage(postsMessages.archiveLink, { count: archivedCount })}
        </button>
      )}
    </div>
  );
};

export const PostsToolbar = TypedMemo(PostsToolbarComponent);
